<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <v-row align="center">
          <v-col cols="12" md="4">
            <div class="label mb-2">Name</div>
            <v-text-field
              v-model="form.name"
              :rules="[validationRules.required]"
              :error="form.errors.name"
              :error-messages="form.errors.name"
              solo
              flat
              outlined
              dense
              placeholder="Teacher full name"
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">Code</div>
            <v-text-field
              v-model="form.code"
              :rules="[validationRules.required]"
              :error="form.errors.code"
              :error-messages="form.errors.code"
              solo
              flat
              outlined
              dense
              placeholder="Teacher code"
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">Join Date</div>
            <v-menu
              ref="joinDateMenu"
              v-model="joinDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="form.join_date"
                  :rules="[validationRules.required]"
                  :error="form.errors.join_date"
                  :error-messages="form.errors.join_date"
                  label="Join Date"
                  placeholder="Select join date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.join_date"
                @change="$refs.joinDateMenu.save(form.join_date)"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Status</div>
            <v-select
              v-model="form.status"
              :items="statusOptions"
              :rules="[validationRules.required]"
              :error="form.errors.status"
              :error-messages="form.errors.status"
              solo
              flat
              outlined
              dense
              placeholder="Select status"
            />
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12" md="4">
            <div class="label mb-2">Phone Number</div>
            <v-text-field
              v-model="form.phone_number"
              solo
              flat
              outlined
              dense
              placeholder="Phone number"
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">WeChat ID</div>
            <v-text-field
              v-model="form.wechat_id"
              solo
              flat
              outlined
              dense
              placeholder="WeChat ID"
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">Date of Birth</div>
            <v-menu
              ref="dobMenu"
              v-model="dobMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="form.date_of_birth"
                  :rules="[validationRules.required]"
                  :error="form.errors.date_of_birth"
                  :error-messages="form.errors.date_of_birth"
                  label="Date of Birth"
                  placeholder="Select birth date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.date_of_birth"
                @change="$refs.dobMenu.save(form.date_of_birth)"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Gender</div>
            <v-select
              v-model="form.gender"
              :items="genderOptions"
              :rules="[validationRules.required]"
              :error="form.errors.gender"
              :error-messages="form.errors.gender"
              solo
              flat
              outlined
              dense
              placeholder="Gender"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <div class="label mb-2">Email</div>
            <v-text-field
              v-model="form.email"
              :rules="[validationRules.required, validationRules.email]"
              :error="form.errors.email"
              :error-messages="form.errors.email"
              solo
              flat
              outlined
              dense
              placeholder="Email will be used for login"
            />
          </v-col>
          <v-col cols="12" md="4">
            <div class="label mb-2">Password</div>
            <v-text-field
              v-model="form.password"
              solo
              flat
              outlined
              dense
              :disabled="isGeneratePassword"
            />
          </v-col>
          <v-col v-if="mode == 'add'" cols="12" md="4">
            <v-checkbox
              v-model="isGeneratePassword"
              label="Auto generate password?"
              persistent-hint
              hint="Password will be set from birth date with format: yyyymmdd (e.g. 19930524)"
              @change="generatePassword"
            >
            </v-checkbox>
          </v-col>
        </v-row>
        <v-checkbox
          v-model="form.is_native_speaker"
          label="Native speaker?"
          class="mb-4"
          hint="Check to change currency of hourly rate salary"
          persistent-hint
        />
        <h2>Salary Hourly Rate</h2>
        <v-divider class="my-4"></v-divider>
        <v-row>
          <v-col cols="12" md="2">
            <div class="label mb-2">Private Class</div>
            <v-text-field
              v-model="form.private_hourly_rate"
              :rules="[validationRules.required]"
              :error="form.errors.private_hourly_rate"
              :error-messages="form.errors.private_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Private class hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Group of 2</div>
            <v-text-field
              v-model="form.group_2_hourly_rate"
              :error="form.errors.group_2_hourly_rate"
              :error-messages="form.errors.group_2_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Group of 2 class hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Group of 3</div>
            <v-text-field
              v-model="form.group_3_hourly_rate"
              :error="form.errors.group_3_hourly_rate"
              :error-messages="form.errors.group_3_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Group of 3 class hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Group of 4</div>
            <v-text-field
              v-model="form.group_4_hourly_rate"
              :error="form.errors.group_4_hourly_rate"
              :error-messages="form.errors.group_4_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Group of 4 class hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Group of 5</div>
            <v-text-field
              v-model="form.group_5_hourly_rate"
              :error="form.errors.group_5_hourly_rate"
              :error-messages="form.errors.group_5_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Group of 5 class hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Group of 6</div>
            <v-text-field
              v-model="form.group_6_hourly_rate"
              :error="form.errors.group_6_hourly_rate"
              :error-messages="form.errors.group_6_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Group of 6 class hourly rate"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="2">
            <div class="label mb-2">Small Group Class (2 - 6 students)</div>
            <v-text-field
              v-model="form.small_group_hourly_rate"
              :error="form.errors.small_group_hourly_rate"
              :error-messages="form.errors.small_group_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Small Group class hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Large Group Class (> 6 students)</div>
            <v-text-field
              v-model="form.large_group_hourly_rate"
              :error="form.errors.large_group_hourly_rate"
              :error-messages="form.errors.large_group_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Large Group class hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Custom 1</div>
            <v-text-field
              v-model="form.custom_1_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Custom 1 hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Custom 2</div>
            <v-text-field
              v-model="form.custom_2_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Custom 2 hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Custom 3</div>
            <v-text-field
              v-model="form.custom_3_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Custom 3 hourly rate"
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Custom 4</div>
            <v-text-field
              v-model="form.custom_4_hourly_rate"
              class="input-right"
              solo
              flat
              outlined
              dense
              :prefix="form.currency_code"
              type="number"
              placeholder="Custom 4 hourly rate"
            />
          </v-col>
        </v-row>
        <v-divider class="my-4"></v-divider>

        <div class="label mb-2">Work Contract File</div>
        <file-uploader
          :value="form.work_contract_file_url"
          placeholder="Upload PDF document"
          class="mb-4"
          @input="
            uploadFile({
              file: $event,
              prop: 'work_contract_file_url',
            })
          "
        ></file-uploader>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
              >Save Changes</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>

<script>
import constant from '@/constant'
import { mapActions } from 'vuex'
export default {
  name: 'TeacherForm',
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    teacher: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      dobMenu: false,
      joinDateMenu: false,
      form: this.$inertia.form({
        name: null,
        code: null,
        email: null,
        phone_number: null,
        gender: null,
        date_of_birth: null,
        password: null,
        is_native_speaker: false,
        currency_code: 'IDR',
        private_hourly_rate: 0,
        small_group_hourly_rate: 0,
        large_group_hourly_rate: 0,
        group_2_hourly_rate: 0,
        group_3_hourly_rate: 0,
        group_4_hourly_rate: 0,
        group_5_hourly_rate: 0,
        group_6_hourly_rate: 0,
        custom_1_hourly_rate: 0,
        custom_2_hourly_rate: 0,
        custom_3_hourly_rate: 0,
        custom_4_hourly_rate: 0,
        work_contract_file_url: null,
        join_date: null,
        status: null,
        profile_image_url: null,
        wechat_id: null,
      }),
      isGeneratePassword: false,
    }
  },
  computed: {
    isReadonly() {
      return this.mode === 'view'
    },
    genderOptions() {
      return constant.genderOptions
    },
    statusOptions() {
      return constant.teacherStatusOptions
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
    'form.is_native_speaker': function (value) {
      console.log(value)
      if (value) {
        this.form.currency_code = 'RMB'
      } else {
        this.form.currency_code = 'IDR'
      }
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({ upload: 'storage/upload' }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.teacher) {
        console.error(
          `teacher props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    init() {
      if (this.mode != 'add' && this.teacher != null) {
        _.merge(this.form, this.teacher)
      }
    },
    generatePassword() {
      this.form.password = this.form.date_of_birth
        ? this.form.date_of_birth.replaceAll('-', '')
        : null
    },
    async uploadFile({ file, prop }) {
      const responseData = await this.upload({ file, baseDir: 'teacher' })
      if (responseData) {
        this.form[prop] = responseData.url
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    async submit() {
      if (this.$refs.form.validate()) {
        if (this.mode === 'add') {
          this.form.post('/teachers')
        } else if (this.mode === 'edit') {
          this.form.put(`/teachers/${this.teacher.id}`)
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.input-right {
  :deep(input) {
    text-align: right;
  }
}
</style>
